// GRN phase 1 (2026-10-21): rules and labels shared by the purchase-path step, the direct purchase screen and the
// receive window. The server checks the same rules (receiving.service); these only make the screens explain them.
import React, { useRef, useState } from 'react';
import { Button, message } from 'antd';
import { CameraOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { PaymentMethod, ProofFile, RejectOutcome, uploadReceiptProof } from '../../api';

export const MAX_DAYS_BACK = 7;     // g06
export const REASON_AFTER_DAYS = 2; // g06

export const PAYMENT_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'on_account', label: 'On account' },
];
export const PAYMENT_LABEL: Record<string, string> = Object.fromEntries(PAYMENT_OPTIONS.map((o) => [o.value, o.label]));

export const REJECT_REASONS = [
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'short', label: 'Short' },
  { value: 'quality', label: 'Quality' },
  { value: 'other', label: 'Other' },
];

// Rejected goods mockup (2026-10-24) wording; D3: a discount keeps the full price paid; already paid → the vendor owes us
// the amount, not paid yet → we pay it less (Ahmed, 24 Oct).
// Step 4a (Ahmed, 25 Oct; mockup DyrAbPJDgtF8t6mjFfytkv "Door & returns" 1): at the door faulty goods are replaced, short-closed or kept at a
// discount. A return is a later step, for accepted goods; rejected goods are not kept, so nothing is written off.
export const OUTCOME_OPTIONS: Array<{ value: RejectOutcome; label: string; hint: string }> = [
  { value: 'replacement', label: 'Vendor replaces it', hint: 'The line stays open; the vendor brings new ones and takes these back.' },
  { value: 'short_close', label: 'Short close', hint: 'The vendor will not replace it. The quantity closes on the PO line now, logged in the PO history.' },
  { value: 'discount', label: 'Keep at a discount', hint: 'We keep the goods at the price paid, for an agreed amount off: the vendor owes it to us if already paid, or we pay it less.' },
];
// Labels of every outcome a receipt line can carry, including those saved before step 4a.
export const OUTCOME_LABEL: Record<string, string> = {
  ...Object.fromEntries(OUTCOME_OPTIONS.map((o) => [o.value, o.label])),
  return: 'Return to vendor', writeoff: 'Write off',
};
export const MR_ACTION_OPTIONS = [
  { value: 'not_needed' as const, label: 'Requester: no longer needed' },
  { value: 'reorder' as const, label: 'Requester: order it again' },
];

export const PATH_LABEL: Record<string, string> = { direct: 'Direct purchase', order: 'Vendor order' };

/** One choice under "What happens next": the door's rejection window and "Choose another outcome" on the receipt page. */
export const OutcomeCard: React.FC<{ label: string; on: boolean; onPick: () => void; children?: React.ReactNode }> = ({ label, on, onPick, children }) => {
  const pick = () => { if (!on) onPick(); };
  return (
    <div
      role="radio"
      aria-checked={on}
      tabIndex={0}
      onClick={pick}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); pick(); } }}
      style={{
        border: on ? '2px solid #1677ff' : '1px solid #d9d9d9', background: on ? '#e6f4ff' : '#fff', borderRadius: 10, padding: on ? 11 : 12,
        display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', gap: 10, cursor: 'pointer',
      }}
    >
      <span aria-hidden style={{ width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box', border: on ? '6px solid #1677ff' : '2px solid #bfbfbf', marginTop: 2 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <b>{label}</b>
        {children}
      </div>
    </div>
  );
};

/** Days a chosen date lies before today (0 = today). */
export const daysBack = (d: Dayjs | null) => (d ? dayjs().startOf('day').diff(d.startOf('day'), 'day') : 0);
/** DatePicker: today and up to 7 days back. */
export const outsideReceiptWindow = (d: Dayjs) => d.isAfter(dayjs(), 'day') || d.isBefore(dayjs().subtract(MAX_DAYS_BACK, 'day'), 'day');
export const isoDay = (d: Dayjs | null) => (d ? d.format('YYYY-MM-DD') : '');
export const money = (n: number, decimals = 2) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/** Receipt / delivery note photos: uploaded as soon as they are picked (private storage), listed with a remove button. */
export const ProofPicker: React.FC<{ value: ProofFile[]; onChange: (files: ProofFile[]) => void; label?: string; upload?: (files: File[]) => Promise<ProofFile[]> }> = ({ value, onChange, label, upload }) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (list: FileList | null) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    setBusy(true);
    try {
      // Receipts by default; payments pass their own upload (private folder payments/, step 3).
      const up = await (upload || uploadReceiptProof)(files);
      onChange([...value, ...up]);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not add the file');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value.map((f, i) => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #b7eb8f', background: '#f6ffed', borderRadius: 8, padding: '6px 10px' }}>
          <FileOutlined style={{ color: '#389e0d' }} aria-hidden />
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
          <span style={{ fontSize: 12, color: '#5f6672' }}>{Math.max(1, Math.round(f.size / 1024))} KB</span>
          <Button type="text" size="small" icon={<DeleteOutlined />} aria-label={`Remove ${f.name}`} onClick={() => onChange(value.filter((_, j) => j !== i))} />
        </div>
      ))}
      {/* No capture attribute: phones then offer both the camera and photos already taken. */}
      <input ref={input} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => pick(e.target.files)} />
      <Button icon={<CameraOutlined />} loading={busy} onClick={() => input.current?.click()} style={{ alignSelf: 'flex-start' }}>
        {value.length ? 'Add another' : label || 'Add a photo'}
      </Button>
    </div>
  );
};
